import type { PatchOperation } from "@medplum/core";
import type {
  Account,
  ActivityDefinition,
  Appointment,
  Basic,
  Communication,
  CommunicationRequest,
  Extension,
  HealthcareService,
  Location,
  MedicationDispense,
  ObservationDefinition,
  Organization,
  Patient,
  Practitioner,
  PractitionerRole,
  QuestionnaireResponseItem,
  Schedule,
  ServiceRequest,
  Task,
} from "@medplum/fhirtypes";

export type ResourceWithIdentifierAndExtension =
  | HealthcareService
  | Organization
  | Communication
  | CommunicationRequest
  | Patient
  | Practitioner
  | PractitionerRole
  | Account
  | Basic
  | Schedule
  | ServiceRequest
  | MedicationDispense
  | Task
  | ActivityDefinition
  | ObservationDefinition
  | Location
  | Appointment;

export interface IdentifierPatchRequest {
  resource: ResourceWithIdentifierAndExtension;
  system: string;
  value: string | null;
}

export interface ExtensionPatchRequest {
  resource: ResourceWithIdentifierAndExtension;
  url: string;
  value: any | null;
}

/**
 * Builds a type guard from an object whose values are string literals.
 *
 * Example:
 * const InviteStatus = { Pending: 'Pending', Invited: 'Invited' } as const;
 * const isInviteStatus = createObjectValueGuard(InviteStatus);
 */
export function createObjectValueGuard<const O extends Record<string, string>>(obj: O) {
  const values = new Set(Object.values(obj));
  return (value: unknown): value is O[keyof O] => typeof value === "string" && values.has(value);
}

export function findExtension(
  resource: ResourceWithIdentifierAndExtension,
  url: string,
): Extension | undefined {
  return resource.extension?.find((extension) => extension.url === url);
}

export function getIdentifierPatchOperations(
  resource: ResourceWithIdentifierAndExtension,
  identifierPatchRequests: IdentifierPatchRequest[],
): PatchOperation[] {
  if (identifierPatchRequests.length === 0) {
    return [];
  }

  const identifiers = resource.identifier;

  if (!identifiers) {
    // filter out any requests that have no value
    const identifierPatchRequestsWithValues = identifierPatchRequests.filter(
      (request) => request.value !== null,
    );

    // need to add the identifiers array explicitly
    return [
      {
        op: "add",
        path: "/identifier",
        value: identifierPatchRequestsWithValues.map((request) => ({
          system: request.system,
          value: request.value,
        })),
      },
    ];
  }

  const patchOperations: PatchOperation[] = [];

  for (const request of identifierPatchRequests) {
    const index = identifiers.findIndex((identifier) => identifier.system === request.system);

    if (index !== -1) {
      if (request.value === null) {
        patchOperations.push({
          op: "remove",
          path: `/identifier/${index}`,
        });
      } else {
        patchOperations.push({
          op: "replace",
          path: `/identifier/${index}`,
          value: { system: request.system, value: request.value },
        });
      }
    } else {
      if (request.value !== null && request.value !== undefined) {
        // add to end of array
        patchOperations.push({
          op: "add",
          path: "/identifier/-",
          value: { system: request.system, value: request.value },
        });
      }
    }
  }
  return sortPatchOperations(patchOperations);
}

export function getExtensionPatchOperations(
  resource: ResourceWithIdentifierAndExtension,
  extensionPatchRequests: ExtensionPatchRequest[],
): PatchOperation[] {
  if (extensionPatchRequests.length === 0) {
    return [];
  }

  const extensions = resource.extension;

  if (!extensions) {
    const extensionsToAdd = extensionPatchRequests.filter(
      (request) => request.value !== null && request.value !== undefined,
    );

    if (extensionsToAdd.length === 0) {
      return [];
    }

    // need to add the extensions array explicitly
    return [
      {
        op: "add",
        path: "/extension",
        value: extensionsToAdd.map((request) => ({
          url: request.url,
          ...getValueObject(request.value),
        })),
      },
    ];
  }

  const patchOperations: PatchOperation[] = [];

  for (const request of extensionPatchRequests) {
    const index = extensions.findIndex((extension) => extension.url === request.url);

    if (index !== -1) {
      if (request.value === null) {
        patchOperations.push({
          op: "remove",
          path: `/extension/${index}`,
        });
      } else {
        patchOperations.push({
          op: "replace",
          path: `/extension/${index}`,
          value: { url: request.url, ...getValueObject(request.value) },
        });
      }
    } else {
      if (request.value !== null && request.value !== undefined) {
        // add to end of array
        patchOperations.push({
          op: "add",
          path: "/extension/-",
          value: { url: request.url, ...getValueObject(request.value) },
        });
      }
    }
  }
  return sortPatchOperations(patchOperations);
}

/** Trailing array index of a patch path (`/identifier/2` -> 2). `null` when the path isn't indexed. */
function getPatchPathIndex(path: string | undefined): number | null {
  const lastSegment = path?.split("/").pop();
  if (!lastSegment?.length) return null;

  const index = Number(lastSegment);
  return Number.isInteger(index) ? index : null;
}

function sortPatchOperations(patchOperations: PatchOperation[]): PatchOperation[] {
  // sort patchOperations by op: add, replace, remove
  // so the patch is applied in the correct order
  const opOrder = ["add", "replace", "remove"];

  patchOperations.sort((a, b) => {
    const byOp = opOrder.indexOf(a.op) - opOrder.indexOf(b.op);
    if (byOp !== 0) return byOp;
    if (a.op !== "remove") return 0;

    // Every index was resolved against the *pre-patch* array, but ops apply sequentially:
    // removing a lower index first shifts everything after it down, so the next removal deletes
    // its neighbour instead of its target. Descending order keeps each remaining index valid.
    const aIndex = getPatchPathIndex(a.path);
    const bIndex = getPatchPathIndex(b.path);
    if (aIndex === null || bIndex === null) return 0;

    return bIndex - aIndex;
  });
  return patchOperations;
}

function getValueObject(value: any): { [key: string]: any } {
  if (isCustomCodeType(value)) {
    return { valueCode: value };
  } else if (typeof value === "string") {
    return { valueString: value };
  } else if (typeof value === "number") {
    return { valueDecimal: value };
  } else if (typeof value === "boolean") {
    return { valueBoolean: value };
  } else {
    // Handle other types as needed
    return { value };
  }
}

// TODO not amazing but it works for now. Maybe more type safety from enums?
function isCustomCodeType(value: any): boolean {
  const customCodeTypes = [
    "INVITED",
    "COMPLETED", // OnboardingStatus values
    "one-time",
    "monthly subscription",
    "quarterly subscription",
    "six-month subscription",
    "yearly subscription", // PaymentSchedule values
  ];
  return customCodeTypes.includes(value);
}

export const utilsTest = {
  getValueObject,
};

/**
 * Converts a QuestionnaireResponseItem into a data object.
 *
 * Output example:
 * {
 *   "patient": {
 *     "firstName": "John",
 *     "lastName": "Doe",
 *     "birthDate": "1990-01-01",
 *     "address": "123 Main St, Anytown, USA",
 *     "email": "john.doe@example.com",
 *     "primaryPhone": "123-456-7890",
 *     "secondaryPhone": "098-765-4321"
 *   },
 *   "emergencyContact": {
 *     "name": "Jane Doe",
 *     "relationship": "Sibling",
 *     "phone": "098-765-4321"
 *   },
 *   "medications": [
 *     {
 *       "name": "Aspirin",
 *       "icd10": "123456",
 *       "dosage": "100mg",
 *       "frequency": "daily"
 *     }
 *   ]
 * }
 *
 * @param item - The QuestionnaireResponseItem.
 * @returns A flat object.
 */
export function getQuestionnaireResponseData(
  item: QuestionnaireResponseItem[],
): Record<string, any> {
  const data: Record<string, any> = {};

  for (const field of item) {
    const key = field.linkId.replace("[]", "");
    const itemData = getItemData(field);

    // This stores the item data in the data map.
    // Previously we were using valueCodeableConcept to store the code for the answer. I'm not sure
    // how that worked because it isn't part of the standard. FHIR R4 defines the valueCoding field
    // for QuestionnaireResponseItemAnswer to store codes.
    // Here we keep the mapping from valueCodeableConcept to valueCoding for backwards compatibility.
    data[key] = Array.isArray(itemData)
      ? itemData.map((answer) => ({
          ...answer,
          valueCoding: answer?.valueCodeableConcept?.[0],
        }))
      : {
          ...itemData,
          valueCoding: itemData?.valueCodeableConcept?.[0],
        };
  }

  return data;
}

/**
 * Converts a QuestionnaireResponseItem into a data object.
 *
 * @param item - The QuestionnaireResponseItem.
 * @param debug - Whether to log debug information.
 * @returns A flat object.
 */
function getItemData(
  item: QuestionnaireResponseItem,
  debug = false,
): Record<string, any> | Record<string, any>[] {
  // If the item is a list, get the data for each item in the list.
  if (item.linkId.endsWith("[]")) return item.item?.map((item) => getItemData(item, debug)) || [];

  // If the item has a single answer, return the answer.
  if (item.answer) {
    if (debug) console.log("Answers", item.answer);
    return {
      ...item.answer[0],
      key: item.linkId.split(".").pop() ?? item.linkId,
    };
  }

  // If the item contains a group of related items (such as medication name, code, dosage, etc), get
  // the data for each item in the group.
  const data: Record<string, any> = {};
  const childItems = item.item ?? [];

  for (let index = 0; index < childItems.length; index++) {
    if (debug) console.log(childItems[index]);
    const field = childItems[index];
    const key = field.linkId.split(".").pop() ?? field.linkId;
    data[key] = {
      ...getItemData(field, debug || key.includes("medications")),
      text: field.text,
      key,
      index,
    };
  }
  return data;
}
